import Hotjar from '@hotjar/browser';
import Clarity from '@microsoft/clarity';
import posthog from 'posthog-js';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { logger } from './logger';
import { identifyFeaturebaseUser, clearFeaturebaseIdentity } from './featurebase';

const HOTJAR_SITE_ID = 6609488;
const HOTJAR_VERSION = 6;
const CLARITY_PROJECT_ID = 'uss92h7660';
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY;
const GA_MEASUREMENT_ID = 'G-XKH2ZKT2LG'; // Mellow Web GA4 (https://app.mellowly.app)

let hotjarInitialized = false;
let clarityInitialized = false;
let posthogInitialized = false;
let amplitudeInitialized = false;
let gaInitialized = false;

/**
 * Initialize Hotjar tracking
 * Safe to call multiple times - will only initialize once
 */
export function initHotjar() {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (hotjarInitialized) {
    return;
  }

  try {
    Hotjar.init(HOTJAR_SITE_ID, HOTJAR_VERSION);
    hotjarInitialized = true;
    console.log('[Hotjar] Initialized successfully');
  } catch (error) {
    logger.error('[Hotjar] Initialization failed', { error: error.message });
  }
}

/**
 * Initialize Microsoft Clarity tracking
 * Safe to call multiple times - will only initialize once
 */
export function initClarity() {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (clarityInitialized) {
    return;
  }

  try {
    Clarity.init(CLARITY_PROJECT_ID);
    clarityInitialized = true;
    console.log('[Clarity] Initialized successfully');
  } catch (error) {
    logger.error('[Clarity] Initialization failed', { error: error.message });
  }
}

/**
 * Initialize PostHog analytics
 * Safe to call multiple times - will only initialize once
 */
export function initPostHog() {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (posthogInitialized) {
    return;
  }

  // Check if env vars are set
  if (!POSTHOG_KEY || !POSTHOG_HOST) {
    console.warn('[PostHog] Missing API key or host');
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only', // Only create profiles for identified users
      capture_pageview: true, // Auto-capture page views
      capture_pageleave: true, // Track when users leave
      autocapture: true, // Auto-capture clicks, form submissions, etc.
    });
    posthogInitialized = true;
    console.log('[PostHog] Initialized successfully');
  } catch (error) {
    logger.error('[PostHog] Initialization failed', { error: error.message });
  }
}

/**
 * Initialize Amplitude analytics
 * Safe to call multiple times - will only initialize once
 */
export function initAmplitude() {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (amplitudeInitialized) {
    return;
  }

  // Check if API key is set
  if (!AMPLITUDE_API_KEY) {
    console.debug('[Amplitude] Missing API key, skipping init');
    return;
  }

  try {
    amplitude.add(sessionReplayPlugin({
      sampleRate: 1,
      privacyConfig: { defaultMaskLevel: 'light' },
    }));
    amplitude.init(AMPLITUDE_API_KEY, {
      logLevel: amplitude.Types.LogLevel.None,
      fetchRemoteConfig: true,
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: true,
      },
      serverZone: 'EU',
    });
    amplitudeInitialized = true;
  } catch (error) {
    logger.error('[Amplitude] Initialization failed', { error: error.message });
  }
}

/**
 * Initialize Google Analytics 4
 * Safe to call multiple times - will only initialize once
 */
export function initGA4() {
  // Only run in browser
  if (typeof window === 'undefined') {
    return;
  }

  // Only initialize once
  if (gaInitialized) {
    return;
  }

  try {
    // Create script element for gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      // Best practices for GA4
      send_page_view: true, // Auto-send page views
      anonymize_ip: true, // Privacy compliance
      allow_google_signals: true, // Enable remarketing features
      allow_ad_personalization_signals: true, // Enable ad personalization
    });

    gaInitialized = true;
    console.log('[GA4] Initialized successfully');
  } catch (error) {
    logger.error('[GA4] Initialization failed', { error: error.message });
  }
}

/**
 * Get PostHog instance for cross-domain tracking
 */
export function getPostHog() {
  return posthog;
}

/**
 * Get Amplitude instance for custom tracking
 */
export function getAmplitude() {
  return amplitude;
}

/**
 * Capture cross-domain tracking parameters from URL hash
 * Call this on app initialization to continue tracking from landing page
 */
export function captureCrossDomainParams() {
  if (!posthogInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    const hash = window.location.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.substring(1));
    const distinctId = params.get('ph_distinct_id');
    const sessionId = params.get('ph_session_id');

    if (distinctId && sessionId) {
      // Set the distinct_id and session_id from landing page
      posthog.identify(distinctId);
      posthog.set_config({ session_id: sessionId });

      console.log('[PostHog] Cross-domain tracking captured:', { distinctId, sessionId });

      // Clean up URL hash
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (error) {
    logger.error('[PostHog] Failed to capture cross-domain params', { error: error.message });
  }
}

/**
 * Track SPA route changes
 * Call this on React Router navigation
 */
export function trackPageView(url) {
  if (!hotjarInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    // Hotjar's stateChange API for SPA navigation
    if (window.hj) {
      window.hj('stateChange', url);
    }
  } catch (error) {
    logger.error('[Hotjar] Page view tracking failed', { error: error.message, url });
  }
}

/**
 * Identify user across all analytics services
 * Call this after successful login/registration
 * @param {string} userId - Unique user ID
 * @param {object} userData - User properties (username, email, etc.)
 * @param {object} enrichedData - Additional user context (languages, XP, cards, credits)
 */
export function identifyUser(userId, userData = {}, enrichedData = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Google Analytics 4
    if (gaInitialized && window.gtag) {
      window.gtag('set', 'user_properties', {
        user_id: userId,
        username: userData.username,
      });
      console.log('[GA4] User identified:', userId);
    }

    // Amplitude (CRITICAL - user journey tracking)
    if (amplitudeInitialized) {
      amplitude.setUserId(userId);

      // Build comprehensive user properties
      const identify = new amplitude.Identify();

      // Basic user info
      if (userData.email) identify.set('email', userData.email);
      if (userData.username) identify.set('username', userData.username);

      // Learning context (Plan A - Essential User Context)
      if (enrichedData.current_language_code) identify.set('current_language_code', enrichedData.current_language_code);
      if (enrichedData.current_language_level) identify.set('current_language_level', enrichedData.current_language_level);
      if (enrichedData.native_language) identify.set('native_language', enrichedData.native_language);
      if (enrichedData.total_languages !== undefined) identify.set('total_languages', enrichedData.total_languages);

      // Engagement metrics (Plan A)
      if (enrichedData.total_cards !== undefined) identify.set('total_cards', enrichedData.total_cards);
      if (enrichedData.total_xp !== undefined) identify.set('total_xp', enrichedData.total_xp);
      if (enrichedData.current_streak !== undefined) identify.set('current_streak', enrichedData.current_streak);
      if (enrichedData.days_since_signup !== undefined) identify.set('days_since_signup', enrichedData.days_since_signup);

      // Monetization (Plan A)
      if (enrichedData.credit_balance !== undefined) identify.set('credit_balance', enrichedData.credit_balance);
      if (enrichedData.total_credits_purchased !== undefined) identify.set('total_credits_purchased', enrichedData.total_credits_purchased);
      if (enrichedData.is_paying_user !== undefined) identify.set('is_paying_user', enrichedData.is_paying_user);

      amplitude.identify(identify);
      console.log('[Amplitude] User identified with enriched properties:', userId);
    }

    // PostHog (CRITICAL - required for retention charts)
    if (posthogInitialized) {
      posthog.identify(userId, {
        username: userData.username,
        email: userData.email,
        ...enrichedData, // Include all enriched data
      });
      console.log('[PostHog] User identified:', userId);
    }

    // Clarity (Optional - better session filtering)
    if (clarityInitialized && window.clarity) {
      window.clarity('identify', userId);
      console.log('[Clarity] User identified:', userId);
    }

    // Hotjar (Optional - user attributes for targeting)
    if (hotjarInitialized && window.hj) {
      window.hj('identify', userId, {
        username: userData.username,
        email: userData.email,
      });
      console.log('[Hotjar] User identified:', userId);
    }

    // Featurebase (Feedback widget)
    identifyFeaturebaseUser(userId, {
      email: userData.email,
      name: userData.username,
      profilePicture: userData.avatar_url,
    });
  } catch (error) {
    logger.error('[Analytics] User identification failed', { error: error.message, userId });
  }
}

/**
 * Update user properties (for incremental changes)
 * Use this when user properties change (e.g., language switch, XP gain)
 * @param {object} properties - Properties to update
 */
export function updateUserProperties(properties) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Amplitude - update user properties
    if (amplitudeInitialized) {
      const identify = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value);
      });
      amplitude.identify(identify);
      console.log('[Amplitude] User properties updated:', properties);
    }

    // PostHog - update person properties
    if (posthogInitialized) {
      posthog.setPersonProperties(properties);
      console.log('[PostHog] Person properties updated:', properties);
    }
  } catch (error) {
    logger.error('[Analytics] Failed to update user properties', { error: error.message, properties });
  }
}

/**
 * Increment user properties (for counters)
 * Use this for metrics that increase over time (XP, cards, etc.)
 * @param {object} properties - Properties to increment { property: amount }
 */
export function incrementUserProperties(properties) {
  if (typeof window === 'undefined' || !amplitudeInitialized) {
    return;
  }

  try {
    const identify = new amplitude.Identify();
    Object.entries(properties).forEach(([key, value]) => {
      identify.add(key, value);
    });
    amplitude.identify(identify);
    console.log('[Amplitude] User properties incremented:', properties);
  } catch (error) {
    logger.error('[Amplitude] Failed to increment user properties', { error: error.message, properties });
  }
}

/**
 * Clear user identity on logout
 * Resets analytics to anonymous tracking
 */
export function clearUserIdentity() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Amplitude - reset to anonymous
    if (amplitudeInitialized) {
      amplitude.reset();
      console.log('[Amplitude] User identity cleared');
    }

    // PostHog - reset to anonymous
    if (posthogInitialized) {
      posthog.reset();
      console.log('[PostHog] User identity cleared');
    }

    // Clarity - no explicit reset API, will auto-reset on new session
    // Hotjar - no explicit reset API, will auto-reset on new session

    // Featurebase - clear identity
    clearFeaturebaseIdentity();

    console.log('[Analytics] User identity cleared');
  } catch (error) {
    logger.error('[Analytics] Failed to clear user identity', { error: error.message });
  }
}

/**
 * Update super properties (sent with every event)
 * Use this for frequently changing context like current language
 * @param {object} properties - Properties to register
 */
export function updateSuperProperties(properties) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    posthog.register(properties);
    console.log('[PostHog] Super properties updated:', properties);
  } catch (error) {
    logger.error('[PostHog] Failed to update super properties', { error: error.message, properties });
  }
}

/**
 * Set person properties (permanent user attributes)
 * Use this for static user data like email, signup date
 * @param {object} properties - Properties to set
 */
export function setPersonProperties(properties) {
  if (typeof window === 'undefined' || !posthogInitialized) {
    return;
  }

  try {
    posthog.setPersonProperties(properties);
    console.log('[PostHog] Person properties set:', properties);
  } catch (error) {
    logger.error('[PostHog] Failed to set person properties', { error: error.message, properties });
  }
}

/**
 * Track custom event across all analytics platforms
 * @param {string} eventName - Event name
 * @param {object} properties - Event properties
 */
export function trackEvent(eventName, properties = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // Google Analytics 4
    if (gaInitialized && window.gtag) {
      window.gtag('event', eventName, properties);
      console.log('[GA4] Event tracked:', eventName, properties);
    }

    // Amplitude - primary event tracking
    if (amplitudeInitialized) {
      amplitude.track(eventName, properties);
      console.log('[Amplitude] Event tracked:', eventName, properties);
    }

    // PostHog - secondary tracking for cross-validation
    if (posthogInitialized) {
      posthog.capture(eventName, properties);
      console.log('[PostHog] Event tracked:', eventName, properties);
    }
  } catch (error) {
    logger.error('[Analytics] Failed to track event', { error: error.message, eventName, properties });
  }
}
